import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { ServiceIcon } from "../components/ServiceIcon";
import { ChatPanel } from "../components/ChatPanel";

export default function Home() {
  const [services, setServices] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="mb-6 inline-flex items-center gap-2 border border-zinc-200 px-3 py-1.5">
              <span className="h-1.5 w-1.5 bg-accentred" />
              <span className="font-mono-label text-[11px] text-zinc-500">IT Services & Solutions</span>
            </div>
            <h1 className="font-heading text-4xl font-black leading-[0.95] tracking-tighter text-zinc-900 sm:text-5xl lg:text-6xl">
              {content.hero_title || "Technology that moves your business forward"}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600">
              {content.hero_subtitle ||
                "From IT support to custom software, call centers to security — we deliver the infrastructure modern companies rely on."}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/lets-build" data-testid="hero-cta-build" className="group flex items-center gap-2 bg-klein px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-kleindark">
                Let's build something <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="border border-zinc-300 px-6 py-3.5 text-sm font-semibold text-zinc-900 transition-colors hover:bg-zinc-50">
                Explore services
              </Link>
            </div>
          </motion.div>

          {/* AI prompt card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="mb-3 flex items-baseline justify-between">
              <span className="font-mono-label text-[11px] text-klein">Talk to us</span>
              <span className="text-xs text-zinc-400">Ask anything about IT</span>
            </div>
            <ChatPanel
              mode="home"
              sessionPrefix="home"
              greeting={content.home_greeting || "Hi! I'm the Genesis IT Solution AI assistant. Ask me about IT support, migrations, call centers, software development, security or anything else — I'm here to help."}
              placeholder="e.g. We need to migrate to Microsoft 365..."
              heightClass="h-[360px]"
            />
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="font-mono-label text-[11px] text-zinc-400">What we do</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">
              Eight ways we power your business
            </h2>
          </div>
          <Link to="/services" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-klein">
            All services <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 border-l border-t border-zinc-200 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <Link
              key={s.id}
              to="/services"
              data-testid={`home-service-${s.slug}`}
              className="group relative border-b border-r border-zinc-200 p-7 transition-colors hover:bg-zinc-50"
            >
              <div className="flex h-11 w-11 items-center justify-center bg-klein/5 text-klein">
                <ServiceIcon name={s.icon} className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-heading text-lg font-semibold text-zinc-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">{s.short_desc}</p>
              <ArrowUpRight className="absolute right-6 top-6 h-4 w-4 text-zinc-300 transition-all group-hover:text-klein" />
            </Link>
          ))}
        </div>
      </section>

      {/* STATS */}
      {content.stats?.length > 0 && (
        <section className="border-y border-zinc-200 bg-zinc-50">
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-zinc-200 px-5 sm:px-8 lg:grid-cols-4">
            {content.stats.map((st, i) => (
              <div key={i} className="px-4 py-10 text-center">
                <div className="font-heading text-4xl font-black tracking-tighter text-klein">{st.value}</div>
                <div className="mt-2 font-mono-label text-[11px] text-zinc-500">{st.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-klein">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center">
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Have a project in mind? Let's scope it together.
          </h2>
          <Link to="/lets-build" data-testid="home-bottom-cta" className="on-accent group flex shrink-0 items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-klein transition-transform hover:-translate-y-0.5">
            Start with our AI consultant <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
